<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { getPreChatFields } from './helpers/preChat';

import PreChatFields from './PreChatFields.vue';
import ButtonNext from 'next/button/Button.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';

const props = defineProps({
  bot: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();

const uiFlags = useMapGetter('wgptBots/getUIFlags');
const customAttributes = useMapGetter('attributes/getAttributes');

const preChatEnabled = ref(false);
const preChatFields = ref([]);

const preChatFieldOptions = computed(() => {
  const { pre_chat_options: preChatOptions } = props.bot;
  return getPreChatFields({
    preChatFields: preChatOptions?.fields,
    customAttributes: customAttributes.value,
  });
});

const tableHeaders = computed(() => [
  '',
  '',
  t('WGPT_BOTS.EDIT.TABS.PRE_CHAT.FORM.SET_FIELDS.TABLE_HEADERS.NAME'),
  t('WGPT_BOTS.EDIT.TABS.PRE_CHAT.FORM.SET_FIELDS.TABLE_HEADERS.DESCRIPTION'),
  t('WGPT_BOTS.EDIT.TABS.PRE_CHAT.FORM.SET_FIELDS.TABLE_HEADERS.TYPE'),
]);

const setDefaults = () => {
  const { pre_chat_enabled: formEnabled } = props.bot;
  preChatEnabled.value = !!formEnabled;
  preChatFields.value = preChatFieldOptions.value || [];
};

const handlePreChatFieldOptions = (event, type, item) => {
  preChatFields.value.forEach((field, index) => {
    if (field.key === item.key) {
      preChatFields.value[index][type] = !item[type];
    }
  });
};

const changePreChatFieldFieldsOrder = updatedPreChatFieldOptions => {
  preChatFields.value = updatedPreChatFieldOptions;
};

const updateBot = async () => {
  try {
    const payload = {
      id: props.bot.id,
      pre_chat_enabled: preChatEnabled.value,
      pre_chat_options: {
        fields: preChatFields.value,
      },
    };
    await store.dispatch('wgptBots/update', payload);
    useAlert(t('WGPT_BOTS.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('WGPT_BOTS.EDIT.API.ERROR_MESSAGE'));
  }
};

watch(() => props.bot, setDefaults);

onMounted(() => {
  setDefaults();
});
</script>

<template>
  <div class="mx-6">
    <SettingsToggleSection
      v-model="preChatEnabled"
      :header="$t('WGPT_BOTS.EDIT.TABS.PRE_CHAT.FORM.ENABLE.LABEL')"
      :description="$t('WGPT_BOTS.EDIT.TABS.PRE_CHAT.DESCRIPTION')"
    />

    <div v-if="preChatEnabled" class="flex items-center my-8 py-1">
      <div class="flex-1 h-px bg-n-weak" />
      <span class="text-body-main text-n-slate-11 px-2">
        {{ $t('WGPT_BOTS.EDIT.TABS.PRE_CHAT.FORM.SET_FIELDS.DESCRIPTION') }}
      </span>
      <div class="flex-1 h-px bg-n-weak" />
    </div>

    <form class="flex flex-col" @submit.prevent="updateBot">
      <div v-if="preChatEnabled">
        <div class="w-full">
          <table
            class="min-w-full table-auto outline outline-1 -outline-offset-1 outline-n-weak rounded-xl"
          >
            <thead>
              <tr class="border-b border-n-weak">
                <th
                  v-for="(header, index) in tableHeaders"
                  :key="index"
                  class="py-3 ltr:pr-4 rtl:pl-4 text-start text-heading-3 text-n-slate-12"
                >
                  {{ header }}
                </th>
              </tr>
            </thead>
            <PreChatFields
              :pre-chat-fields="preChatFields"
              @update="handlePreChatFieldOptions"
              @drag-end="changePreChatFieldFieldsOrder"
            />
          </table>
        </div>
      </div>
      <div class="w-full flex justify-end items-center py-4 mt-2">
        <ButtonNext
          type="submit"
          :label="$t('WGPT_BOTS.EDIT.SUBMIT')"
          :is-loading="uiFlags.isUpdating"
        />
      </div>
    </form>
  </div>
</template>
